<script setup lang="ts">
import type { ErrorCollection } from '@/utils/validator';
import type { CartOrderForm, EventTicketsInventoryItem, TicketSelectionMode } from '@/types/checkout';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';

const props = defineProps({
	tickets: {
		type: Array as PropType<EventTicketsInventoryItem[]>,
		default: () => [],
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	selectionMode: {
		type: Object as PropType<TicketSelectionMode>,
		required: true,
		default: TICKET_SELECTION_MODE.TICKETS,
	},
	isSupportedCountryEvent: {
		type: Boolean,
		default: false,
	},
});

const checkoutStore = useCheckoutStore();
const eventStore = useEventStore();

const isSubmit = ref(false);

const isLoading = computed(() => checkoutStore.ticketsInventoryResponse.isLoading);
const ticketPurchaseLimit = computed(() => eventStore.purchaseLimit);

const rules = computed(() => {
	const isEmptyRule = {
		isEmpty: helpers.withMessage('Please specify the quantity for at least one ticket type.', (cartItems: CartOrderForm[]) => {
			if (!isSubmit.value) {
				return true;
			}

			return cartItems.some((cartItem) => cartItem.quantity > 0);
		}),
	};

	if (props.selectionMode === TICKET_SELECTION_MODE.TICKETS) {
		return {
			orders: {
				...isEmptyRule,
				$each: helpers.forEach({
					quantity: {
						isLessThanAvailableLimit: helpers.withMessage(
							() => `Additional quantity not available.`,
							(qty: number, item: CartOrderForm) => {
								// Skip validation if ticket not on sale
								if (
									item.status !== EVENT_TICKET_STATUS.ON_SALE ||
									qty > ticketPurchaseLimit.value
								) {
									return true;
								}

								return qty <= item.seatAvailable;
							},
						),
						isLessThanTicketLimit: helpers.withMessage(
							() => `Maximum ${ticketPurchaseLimit.value} tickets per order.`,
							(qty: number, item: CartOrderForm) => {
								// Skip validation if ticket not on sale
								if (item.status !== EVENT_TICKET_STATUS.ON_SALE) {
									return true;
								}
								return qty <= ticketPurchaseLimit.value;
							},
						),
					},
				}),
			},
		};
	}
	return {
		orders: {
			...isEmptyRule,
		},
	};
});

const v$ = useVuelidate(rules, { orders: checkoutStore.cart.orders });

const orderErrorMessage = computed(() => {
	const message = getValidationErrorMessage(v$.value.orders.$errors);
	if (message === v$.value.orders.isEmpty?.$message) {
		return message;
	}
	return '';
});

function formatTicketPrice(price: number, symbol: string) {
	return price && price > 0 ? formatCurrency(price, symbol) : 'Free';
}

function formatTicketStatus(status: string) {
	switch (status) {
		case EVENT_TICKET_STATUS.ON_HOLD:
			return 'Paused';
		case EVENT_TICKET_STATUS.SOLD_OUT:
			return 'Sold out';
		default:
			return '';
	}
}

function getQuantityValidationErrorMessages(
	errors: ErrorCollection[],
	index: number,
	propName: string,
) {
	const message = getValidationErrorMessageCollection(errors, index, propName);
	if (!message) {
		return '';
	}
	return message;
}

function isShowTicketLeftWarning(index: number) {
	const seatAvailable = checkoutStore.cart.orders[index].seatAvailable;
	return seatAvailable < ticketPurchaseLimit.value && checkoutStore.cart.orders[index].quantity === seatAvailable;
}

function isShowTicketLimitWarning(index: number) {
	const errorMessage = getQuantityValidationErrorMessages(v$.value.orders.$each?.$response.$errors, index, 'quantity');
	return checkoutStore.cart.orders[index].quantity === ticketPurchaseLimit.value && !errorMessage;
}

function setCartItemsForm(tickets: EventTicketsInventoryItem[]) {
	const mappingCart = new Map();
	if (checkoutStore.cart.orders.length) {
		// Collect existing cart quantity
		checkoutStore.cart.orders.forEach((item) => {
			mappingCart.set(item.id, item.quantity);
		});
	}

	const data = tickets.map((ticket): CartOrderForm => {
		let quantity = props.selectionMode === TICKET_SELECTION_MODE.TOKEN_GATE ? 1 : 0;
		if (mappingCart.size > 0 && mappingCart.has(ticket.id)) {
			quantity = mappingCart.get(ticket.id);
		}
		return {
			id: ticket.id,
			name: ticket.name,
			seatAvailable: ticket.seatsAvailable,
			price: ticket.price,
			discountPrice: ticket.discountPrice,
			quantity,
			status: ticket.status,
			currency: ticket.currency,
		};
	});
	Object.assign(checkoutStore.cart.orders, data);
}

function hasTicketOrdersError() {
	v$.value.$touch();
	isSubmit.value = true;
	return v$.value.$error;
}

function getMaxSelection(form: CartOrderForm) {
	if (props.selectionMode === TICKET_SELECTION_MODE.TICKETS) {
		return form.seatAvailable > ticketPurchaseLimit.value ? ticketPurchaseLimit.value : form.seatAvailable;
	}
	return TOKEN_GATE_PURCHASE_LIMIT;
}

function checkTicketValidity(form: CartOrderForm) {
	// Disable Non-US Event ticket selection
	if (!props.isSupportedCountryEvent) {
		return false;
	}
	return form.status === EVENT_TICKET_STATUS.ON_SALE;
}

function checkStepperValidity(form: CartOrderForm) {
	return form.status === EVENT_TICKET_STATUS.ON_SALE;
}

onMounted(() => {
	setCartItemsForm(props.tickets);
});

watch(
	() => props.tickets,
	(newTickets) => {
		setCartItemsForm(newTickets);
	},
);

defineExpose({
	hasTicketOrdersError,
});
</script>
<template>
  <div class="ticket-selection">
    <template v-if="isLoading">
      <MultipleSkeletonLoader
        class="ticket-loaders"
        rows="3"
        space="0"
      >
        <SentencesSkeletonLoader width="130" />
        <ButtonSkeletonLoader
          width="130"
          height="36"
        />
      </MultipleSkeletonLoader>
    </template>
    <template v-else>
      <span
        v-if="orderErrorMessage"
        class="text-sm error-required"
      >
        {{ orderErrorMessage }}
      </span>
      <div class="tickets">
        <div
          v-for="(form, index) in checkoutStore.cart.orders"
          :key="index"
          class="ticket-item"
        >
          <div
            :class="[
              'left',
              {
                '--not-valid': !checkTicketValidity(form)
              }
            ]"
          >
            <span class="text-md text-medium">{{ form.name }}</span>
            <div>
              <span
                :class="[
                  'text-sm',
                  'text-regular',
                  {
                    'line-through': form.discountPrice !== null
                  }
                ]"
              >{{ formatTicketPrice(form.price, form.currency?.code) }}</span>
              <span
                v-if="form.discountPrice !== null"
                class="text-sm text-regular --discount"
              >{{
                ` ${formatTicketPrice(form.discountPrice, form.currency?.code)}`
              }}</span>
            </div>
          </div>
          <div class="right">
            <template v-if="checkStepperValidity(form)">
              <BaseStepperInput
                v-model="form.quantity"
                :readonly="selectionMode === TICKET_SELECTION_MODE.TOKEN_GATE"
                :max="getMaxSelection(form)"
                :disabled="disabled || !isSupportedCountryEvent"
                :error-message="getQuantityValidationErrorMessages(v$.orders.$each?.$response.$errors, index, 'quantity')"
              />
              <p
                v-if="isShowTicketLeftWarning(index)"
                class="text-sm text-regular --limit-warning"
              >
                Additional quantity not available.
              </p>
              <p
                v-if="isShowTicketLimitWarning(index)"
                class="text-sm text-regular --limit-warning"
              >
                {{ `Maximum ${ticketPurchaseLimit} tickets per order.` }}
              </p>
            </template>
            <span
              v-else
              class="text-md text-medium --not-valid"
            >
              {{ formatTicketStatus(form.status) }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
<style lang="scss" scoped>
.ticket-selection {
	display: flex;
	flex-direction: column;
	gap: spacings-get(4);

	.error-required {
		@include fonts-weight(regular);
		color: colors-get(error, 500);
		white-space: pre-line;
	}

	.tickets {
		.ticket-item {
			display: flex;
			justify-content: space-between;
			gap: spacings-get(3);
			padding: spacings-get(3) 0;

			.left {
				display: flex;
				flex-direction: column;
			}

			.right {
				width: rem(126);
				text-align: end;
			}
		}

		.--limit-warning {
			margin-top: rem(6);
			color: colors-get(gray, 500);
		}
	}

	.ticket-loaders {
		row-gap: spacings-get(6) !important;

		:deep(.skeleton-item) {
			display: flex;
			justify-content: space-between;
		}
	}
}
</style>
